import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { academyPermissions } from "@/lib/academyAccess";
import { money, editionLabel, selectCls, btnBrand } from "@/lib/academyFormat";
import PlanEditor, { cleanPlan } from "@/components/academy/PlanEditor";
import { ACADEMY } from "@/constants/testIds";

// Fix U (2026-10-13): inscribir a un interesado o a un alumno en una fecha.
// El precio sale solo segun la semana (promo 1, promo 2 o normal).
export default function EnrollModal({ open, onOpenChange, lead, student, onDone }) {
  const { user } = useAuth();
  const mine = academyPermissions(user);
  const isAdmin = user?.role === "admin";
  const canDiscount = isAdmin || mine.includes("finance");
  const pickExecutive = isAdmin || mine.some((p) => ["academic", "reception", "finance"].includes(p));
  const [editions, setEditions] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [form, setForm] = useState({ edition_id: "", executive_id: "", discount: "", discount_note: "", notes: "" });
  const [plan, setPlan] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setPlan([]);
    setForm({ edition_id: "", executive_id: lead?.executive_id || student?.executive_id || "", discount: "", discount_note: "", notes: "" });
    api.get("/academy/editions", { params: { upcoming: true } }).then(({ data }) => setEditions(data.filter((e) => e.status === "open"))).catch(() => setEditions([]));
    if (pickExecutive) {
      api.get("/academy/staff").then(({ data }) => setExecutives(data.filter((s) => s.academy_roles.includes("executive")))).catch(() => setExecutives([]));
    }
  }, [open, lead, student, pickExecutive]);

  const edition = editions.find((e) => e.id === form.edition_id);
  const price = edition?.price_today?.price ?? 0;
  const discount = Number(form.discount) || 0;
  const total = Math.max(price - discount, 0);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const who = lead?.name || student?.name || "";

  const save = async () => {
    if (!form.edition_id) {
      setError("Elegí la fecha del curso");
      return;
    }
    setSaving(true);
    setError("");
    const body = { edition_id: form.edition_id, notes: form.notes.trim() || null };
    if (lead) body.lead_id = lead.id;
    if (student) body.student_id = student.id;
    if (pickExecutive && form.executive_id) body.executive_id = form.executive_id;
    if (canDiscount && discount > 0) {
      body.discount = discount;
      body.discount_note = form.discount_note.trim() || null;
    }
    const cleaned = cleanPlan(plan);
    if (cleaned.length) body.payment_plan = cleaned;
    try {
      const { data } = await api.post("/academy/enrollments", body);
      onDone(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={ACADEMY.enrollModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Inscribir a {who}</DialogTitle>
          <DialogDescription className="sr-only">Elegir curso y fecha</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Curso y fecha</Label>
            <select className={selectCls} value={form.edition_id} onChange={set("edition_id")} data-testid={ACADEMY.enrollEditionSelect}>
              <option value="">Elegí una fecha</option>
              {editions.map((e) => (
                <option key={e.id} value={e.id} disabled={e.seats_left === 0}>
                  {e.course_name} · {editionLabel(e)}{e.seats_left === 0 ? " · sin lugares" : e.seats_left != null ? ` · ${e.seats_left} lugares` : ""}
                </option>
              ))}
            </select>
            {editions.length === 0 && <p className="text-xs text-muted-foreground mt-1">No hay fechas abiertas. Cargalas en Cursos y fechas.</p>}
          </div>
          {edition && (
            <div className="rounded-lg bg-[#E9F3F3] p-3 text-sm space-y-1" data-testid={ACADEMY.enrollPriceBox}>
              <p><span className="text-muted-foreground">Precio de esta semana:</span> <strong>{money(price)}</strong> ({edition.price_today.label})</p>
              {edition.location && <p className="text-muted-foreground">{edition.location}</p>}
            </div>
          )}
          {pickExecutive && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Ejecutivo</Label>
              <select className={selectCls} value={form.executive_id} onChange={set("executive_id")} data-testid={ACADEMY.enrollExecutiveSelect}>
                <option value="">Sin ejecutivo</option>
                {executives.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
          )}
          {canDiscount && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Descuento (USD)</Label>
                <Input type="number" min="0" step="0.01" value={form.discount} onChange={set("discount")} data-testid={ACADEMY.enrollDiscountInput} />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Motivo del descuento</Label>
                <Input value={form.discount_note} onChange={set("discount_note")} />
              </div>
            </div>
          )}
          {edition && canDiscount && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Plan de pagos del saldo, después de la reserva (opcional)</Label>
              <PlanEditor value={plan} onChange={setPlan} total={Math.max(total - (edition.reservation_amount ?? 0), 0)} />
            </div>
          )}
          {edition && <p className="text-sm">Total a pagar: <strong data-testid={ACADEMY.enrollTotal}>{money(total)}</strong></p>}
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Notas</Label>
            <Input value={form.notes} onChange={set("notes")} placeholder="Opcional" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={saving} data-testid={ACADEMY.enrollSaveButton}>{saving ? "Inscribiendo..." : "Inscribir"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
